import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Plus, Download, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg } from "@/lib/api";
import { rp, dEN, tglID, BOOKING_STATUS, PAY_STATUS, INVOICE_STATUS } from "@/lib/format";
import { Card, Pill, BigButton, GhostButton, Empty } from "@/components/admin/common";
import BookingForm from "@/components/admin/BookingForm";
import GuestForm from "@/components/admin/GuestForm";
import PaymentDialog from "@/components/admin/PaymentDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription } from "@/components/ui/alert-dialog";

const MoneyBox = ({ title, total, paid, status, due, onPay, testid }) => (
  <Card className="p-4 space-y-3" data-testid={testid}>
    <div className="flex items-center justify-between"><p className="font-extrabold">{title}</p><Pill map={PAY_STATUS} value={status} testid={`${testid}-status`} /></div>
    <div className="grid grid-cols-3 gap-2 text-sm">
      <div><p className="text-slate-500 text-xs">Total</p><p className="font-bold">{rp(total)}</p></div>
      <div><p className="text-slate-500 text-xs">Paid</p><p className="font-bold text-emerald-700">{rp(paid)}</p></div>
      <div><p className="text-slate-500 text-xs">Remaining</p><p className="font-bold text-rose-700" data-testid={`${testid}-remaining`}>{rp(Math.max(total - paid, 0))}</p></div>
    </div>
    {due && <p className="text-xs text-slate-500">Due {dEN(due)}</p>}
    <BigButton onClick={onPay} className="w-full" data-testid={`${testid}-pay-btn`}><Plus className="w-4 h-4" />Record payment</BigButton>
  </Card>
);

const missing = (g) => ["passport", "cert_level", "emergency_phone"].filter((k) => !g[k]);

const GuestList = ({ b, onEdit, onAdd }) => (
  <Card className="p-4 space-y-3" data-testid="guest-manifest-card">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-extrabold">Guest manifest ({b.guests.length}/{b.pax})</p>
      <div className="flex gap-2">
        <GhostButton className="!h-10" onClick={() => download("/admin/guests/export/xlsx", { booking_id: b.id })} data-testid="manifest-export-btn"><Download className="w-4 h-4" />Excel</GhostButton>
        <BigButton className="!h-10" onClick={onAdd} data-testid="add-guest-btn"><Plus className="w-4 h-4" />Add guest</BigButton>
      </div>
    </div>
    {!b.guests.length && <Empty text="No guests added yet." />}
    {b.guests.map((g) => (
      <button key={g.id} onClick={() => onEdit(g)} className="w-full text-left flex items-center gap-3 rounded-xl border border-border p-3 hover:bg-sand" data-testid={`guest-row-${g.id}`}>
        <div className="flex-1 min-w-0">
          <p className="font-bold">{g.full_name}</p>
          <p className="text-sm text-slate-500 truncate">{[g.nationality, g.cert_agency && `${g.cert_agency} ${g.cert_level}`, g.logged_dives != null && `${g.logged_dives} dives`].filter(Boolean).join(" · ")}</p>
        </div>
        {missing(g).length > 0 && <span className="flex items-center gap-1 text-xs font-bold text-rose-700 bg-rose-50 rounded-full px-2 py-1"><AlertCircle className="w-3 h-3" />{missing(g).length} missing</span>}
      </button>
    ))}
  </Card>
);

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [edit, setEdit] = useState(false);
  const [guest, setGuest] = useState(undefined);
  const [pay, setPay] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const load = useCallback(() => api.get(`/admin/bookings/${id}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!b) return <p className="text-slate-500">Loading…</p>;

  const setStatus = async (status) => { await api.patch(`/admin/bookings/${id}/status`, { status }); toast.success("Status updated"); load(); };
  const remove = async () => { await api.delete(`/admin/bookings/${id}`); toast.success("Booking deleted"); navigate("/admin/bookings"); };

  return (
    <div className="max-w-5xl space-y-4" data-testid="booking-detail-page">
      <Link to="/admin/bookings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink" data-testid="back-to-bookings"><ArrowLeft className="w-4 h-4" />Bookings</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500" data-testid="booking-code">{b.code}</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold">{b.customer_name}</h1>
          <p className="text-slate-600 mt-1">{b.trip_type} · {dEN(b.start_date)} – {dEN(b.end_date)} · {b.pax} pax · {b.boat?.name || "No boat"}</p>
          {(b.customer_phone || b.customer_email) && <p className="text-sm text-slate-500">{[b.customer_phone, b.customer_email].filter(Boolean).join(" · ")}</p>}
        </div>
        <div className="flex gap-2 items-center">
          <Select value={b.status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 w-40" data-testid="booking-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(BOOKING_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
          <GhostButton className="!h-11 !px-3" onClick={() => setEdit(true)} data-testid="edit-booking-btn" aria-label="Edit"><Pencil className="w-4 h-4" /></GhostButton>
          <GhostButton className="!h-11 !px-3 text-rose-600" onClick={() => setConfirmDel(true)} data-testid="delete-booking-btn" aria-label="Delete"><Trash2 className="w-4 h-4" /></GhostButton>
        </div>
      </div>
      {b.notes && <Card className="p-4 text-sm whitespace-pre-line">{b.notes}</Card>}
      <div className="grid md:grid-cols-2 gap-3">
        <MoneyBox title="Guest → Indo Aura" total={b.price} paid={b.guest_paid} status={b.guest_pay_status} onPay={() => setPay("guest")} testid="guest-payment" />
        <MoneyBox title="Indo Aura → Boat vendor" total={b.vendor_cost} paid={b.vendor_paid} status={b.vendor_pay_status} due={b.vendor_due_date} onPay={() => setPay("vendor")} testid="vendor-payment" />
      </div>
      {b.payments.length > 0 && (
        <Card className="p-4" data-testid="payments-list">
          <p className="font-extrabold mb-2">Payments (auto-recorded in Keuangan)</p>
          {b.payments.map((t) => (
            <div key={t.id} className="flex justify-between gap-2 py-2 border-t border-border first:border-0 text-sm">
              <span>{tglID(t.date)} · {t.ref} · {t.description}</span>
              <span className={`font-bold ${t.kind === "masuk" ? "text-emerald-700" : "text-rose-700"}`}>{t.kind === "masuk" ? "+" : "-"}{rp(t.amount)}</span>
            </div>
          ))}
        </Card>
      )}
      <GuestList b={b} onAdd={() => setGuest(null)} onEdit={setGuest} />
      <Card className="p-4 space-y-2" data-testid="booking-invoices-card">
        <div className="flex items-center justify-between">
          <p className="font-extrabold">Invoices</p>
          <BigButton className="!h-10" onClick={() => navigate(`/admin/invoices/new?booking=${b.id}`)} data-testid="create-invoice-from-booking-btn"><FileText className="w-4 h-4" />Create invoice</BigButton>
        </div>
        {b.invoices.map((i) => (
          <Link key={i.id} to={`/admin/invoices/${i.id}`} className="flex items-center justify-between rounded-xl border border-border p-3 hover:bg-sand" data-testid={`booking-invoice-${i.id}`}>
            <span className="font-semibold">{i.number}</span><span className="flex items-center gap-2">{rp(i.total)}<Pill map={INVOICE_STATUS} value={i.status} /></span>
          </Link>
        ))}
      </Card>
      <BookingForm open={edit} onClose={() => setEdit(false)} booking={b} onSaved={load} />
      <GuestForm open={guest !== undefined} onClose={() => setGuest(undefined)} bookingId={b.id} guest={guest} onSaved={load} />
      <PaymentDialog open={!!pay} onClose={() => setPay(null)} booking={b} direction={pay} onSaved={setB} />
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="font-admin">
          <AlertDialogHeader><AlertDialogTitle>Delete this booking?</AlertDialogTitle>
            <AlertDialogDescription>Guests on this booking are deleted too. Finance entries are kept but unlinked.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="cancel-delete-booking">Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-rose-600" data-testid="confirm-delete-booking">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
